"use strict";
/**
 * Win-probability page: list today's games, then watch one.
 *
 * **Polling ownership.** This page runs its own timer and it is NOT market
 * polling: it never touches `services/polling-service.ts`, `orderbook.ts` or
 * `state-handler.ts`, and it stops the moment the page is left. The
 * single-owner rule in `ai/GOTCHAS.md` §2 is about market polling; this is a
 * second, independent loop over a read-only route, which is why it is started
 * and stopped here and nowhere else.
 *
 * Nothing here is on the order path.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.showWinProbList = showWinProbList;
exports.showWinProbGame = showWinProbGame;
exports.showHockeyPad = showHockeyPad;
exports.showTapPad = showTapPad;
exports.showNbaPicker = showNbaPicker;
exports.openWinProbRoute = openWinProbRoute;
exports.teardownWinProbPage = teardownWinProbPage;
exports.setupWinProbPage = setupWinProbPage;
const win_prob_1 = require("../api/win-prob");
const server_kind_1 = require("../api/server-kind");
const win_prob_display_1 = require("../ui/win-prob-display");
const page_navigation_1 = require("../ui/page-navigation");
const tap_pad_handler_1 = require("./tap-pad-handler");
const nba_tap_handler_1 = require("./nba-tap-handler");
const nba_tap_handler_v2_1 = require("./nba-tap-handler-v2");
const nba_schedule_1 = require("../api/nba-schedule");
const nba_picker_1 = require("../ui/nba-picker");
const hockey_pad_handler_1 = require("./hockey-pad-handler");
const uuid_1 = require("../services/uuid");
const POLL_MS = 3000;
const LEAGUES = ["nfl", "ncaaf", "nhl"];
const page = { league: "nfl", gameId: null, timer: null, latestView: null, personnelPending: false, localPersonnelWarning: false, qbNameDrafts: { home: "", away: "" } };
/** The NBA picker's day. */
const nba = { date: null };
const TEAM = /^[A-Za-z0-9]{1,5}$/;
/** Bumped by every view, so a slow answer (schedule, leagues) never draws over a newer one. */
let view = 0;
const LABELS = { nfl: "NFL", ncaaf: "NCAAF", nhl: "NHL", nba: "NBA" };
const PAGE_LEAGUES = Object.keys(LABELS);
/**
 * What this server's page offers (A210): the selector's leagues, from the server, and whether the
 * top level has an exit (the recorder has no events page, so its back arrow would be a dead end).
 */
const offer = { leagues: null, loading: null, exit: false };
/** The selector's wanted visibility and value; shown only once the server names two leagues or more. */
const selector = { show: false, value: "nfl" };
/** Whether the current view is a top-level one, whose back arrow leaves the page. */
let topLevel = false;
const el = (id) => document.getElementById(id);
function loadLeagues() {
    if (offer.leagues)
        return Promise.resolve(offer.leagues);
    offer.loading ?? (offer.loading = (0, win_prob_1.fetchWinProbLeagues)()
        .then((served) => {
        // The server's order: its first league is the bare page's (the recorder's NBA).
        const leagues = served.filter((l, i) => PAGE_LEAGUES.includes(l) && served.indexOf(l) === i);
        offer.leagues = leagues;
        drawSelector();
        return leagues;
    })
        .finally(() => { offer.loading = null; }));
    return offer.loading;
}
function drawSelector() {
    const select = el("win-prob-league");
    const leagues = offer.leagues;
    if (select && leagues && [...select.options].map((o) => o.value).join() !== leagues.join()) {
        select.replaceChildren(...leagues.map((l) => new Option(LABELS[l], l)));
    }
    if (select)
        select.value = selector.value;
    const hide = !selector.show || (offer.leagues?.length ?? 0) < 2;
    for (const id of ["win-prob-league-label", "win-prob-league"])
        el(id)?.classList.toggle("hidden", hide);
}
/** Hidden on the pads (the hockey pad, the NBA pad), so a stray tap never leaves a game. */
function showLeagueSelector(show, value = page.league) {
    selector.show = show;
    selector.value = value;
    drawSelector();
}
/** A game goes back to its list; a top-level view leaves the page, where there is a page to leave to. */
function showBack(isTopLevel) {
    topLevel = isTopLevel;
    el("win-prob-back")?.classList.toggle("hidden", isTopLevel && !offer.exit);
}
/** The page without a league named: the last league if this server offers it, else its first. */
function openFirstLeague(preferred) {
    const mine = ++view;
    stopPolling();
    (0, tap_pad_handler_1.closeTapPad)();
    closeNbaPads();
    (0, hockey_pad_handler_1.closeHockeyPad)();
    page.gameId = null;
    showLeagueSelector(false);
    showBack(true);
    setStatus("loading…");
    const body = el("win-prob-body");
    if (body)
        body.innerHTML = "";
    loadLeagues().then((leagues) => {
        if (view !== mine)
            return;
        const pick = [preferred, page.league].find((l) => l && leagues.includes(l)) ?? leagues[0];
        if (pick === "nba")
            showNbaPicker();
        else if (pick) {
            page.league = pick;
            showWinProbList();
        }
        else
            setStatus("no leagues on this server");
    }, (error) => { if (view === mine)
        setStatus(`could not load leagues: ${error.message}`); });
}
function setStatus(message) {
    const node = el("win-prob-status");
    if (node)
        node.textContent = message;
}
function stopPolling() {
    if (page.timer !== null) {
        window.clearInterval(page.timer);
        page.timer = null;
    }
}
/** One timer at a time, whichever view is open. */
function startPolling(tick) {
    stopPolling();
    void tick();
    page.timer = window.setInterval(tick, POLL_MS);
}
async function refreshList() {
    const body = el("win-prob-body");
    if (!body)
        return;
    try {
        const { games, feedStale } = await (0, win_prob_1.fetchWinProbGames)(page.league);
        body.innerHTML = (0, win_prob_display_1.renderGameList)(games);
        setStatus(feedStale ? `${page.league.toUpperCase()} · feed stale` : page.league.toUpperCase());
    }
    catch (error) {
        setStatus(`could not load games: ${error.message}`);
    }
}
async function refreshGame() {
    const body = el("win-prob-body");
    if (!body || !page.gameId || page.personnelPending)
        return;
    const gameId = page.gameId;
    const league = page.league;
    try {
        const view = await (0, win_prob_1.fetchWinProbGame)(league, gameId);
        if (page.gameId !== gameId || page.league !== league)
            return;
        page.latestView = view;
        if (page.localPersonnelWarning) {
            view.quoteReady = false;
            view.personnel = { status: "unavailable", quoteReady: false, reason: "QB event delivery unconfirmed — retry or reconcile" };
        }
        body.innerHTML = (0, win_prob_display_1.renderGameDetail)(view);
        for (const side of ["home", "away"]) {
            const input = body.querySelector(`.wp-qb-name[data-side="${side}"]`);
            if (input)
                input.value = page.qbNameDrafts[side];
        }
        setStatus(`${page.league.toUpperCase()} · updated ${new Date().toLocaleTimeString()}`);
    }
    catch (error) {
        // A game the tracker dropped 404s; say so rather than freezing stale numbers.
        if (page.gameId === gameId && page.league === league)
            setStatus(`game unavailable: ${error.message}`);
    }
}
function showWinProbList() {
    view++;
    showLeagueSelector(true);
    (0, tap_pad_handler_1.closeTapPad)();
    closeNbaPads();
    (0, hockey_pad_handler_1.closeHockeyPad)();
    page.gameId = null;
    showBack(true);
    page.latestView = null;
    page.localPersonnelWarning = false;
    page.qbNameDrafts = { home: "", away: "" };
    (0, page_navigation_1.syncLocation)("win-prob", [], { flatten: true }); // leaving a game: the URL is the list again, Back returns to the game
    startPolling(() => void refreshList());
}
function showWinProbGame(league, gameId) {
    if (league === "nhl")
        return showHockeyPad(gameId); // hockey's one page is the pad (docs/nhl-model/PLAN.md §4.5)
    view++;
    showLeagueSelector(true, league);
    (0, tap_pad_handler_1.closeTapPad)();
    closeNbaPads();
    (0, hockey_pad_handler_1.closeHockeyPad)();
    showBack(false);
    if (page.gameId !== gameId || page.league !== league) {
        page.localPersonnelWarning = false;
        page.latestView = null;
        page.qbNameDrafts = { home: "", away: "" };
    }
    page.league = league;
    page.gameId = gameId;
    (0, page_navigation_1.syncLocation)("win-prob", [league, gameId]);
    startPolling(() => void refreshGame());
}
/** The hockey pad: banner, pre-game screen and the event buttons for one NHL game. */
function showHockeyPad(gameId) {
    view++;
    showLeagueSelector(false);
    stopPolling();
    (0, tap_pad_handler_1.closeTapPad)();
    closeNbaPads();
    showBack(false);
    page.league = "nhl";
    page.gameId = gameId;
    (0, page_navigation_1.syncLocation)("win-prob", ["nhl", gameId, "pad"]);
    setStatus("");
    (0, hockey_pad_handler_1.openHockeyPad)(gameId, () => showWinProbList());
}
/** The tap pad for one game (ai/TAP_PAD_PLAN.md). Needs no live tracker for it. */
function showTapPad(league, gameId) {
    if (league === "nhl")
        return showHockeyPad(gameId);
    view++;
    showLeagueSelector(true, league);
    closeNbaPads();
    showBack(false);
    stopPolling();
    (0, hockey_pad_handler_1.closeHockeyPad)();
    page.league = league;
    page.gameId = gameId;
    (0, page_navigation_1.syncLocation)("win-prob", [league, gameId, "tap"]);
    setStatus("");
    closeNbaPads();
    (0, tap_pad_handler_1.openTapPad)(league, gameId, () => showWinProbGame(league, gameId)); // nhl returned above
}
/** Either NBA pad: they are never open together. */
function closeNbaPads() {
    (0, nba_tap_handler_1.closeNbaTapPad)();
    (0, nba_tap_handler_v2_1.closeNbaTapPadV2)();
}
/**
 * The NBA recorder (ai/nba/N8-taps/PLAN.md): no tracker; teams from the picker or the address. It
 * writes its own address, teams and Pad 2 included, so a reload or a login lands back here (§11.3,
 * §19.5). `espnTip` is the picker's guess for Pad 2's opening question; a direct link has none.
 */
function showNbaTapPad(gameId, teams, version = 1, espnTip = null) {
    view++;
    showLeagueSelector(false);
    stopPolling();
    (0, tap_pad_handler_1.closeTapPad)();
    el("win-prob-back")?.classList.add("hidden"); // the pad's own Exit returns to the picker
    topLevel = false;
    setStatus("");
    (0, page_navigation_1.syncLocation)("win-prob", ["nba", gameId, "tap"]);
    const query = new URLSearchParams({ home: teams.home, away: teams.away, ...(version === 2 ? { pad: "2" } : {}) }).toString();
    window.history.replaceState(window.history.state, "", `/app/win-prob/nba/${encodeURIComponent(gameId)}/tap?${query}`);
    const onExit = () => showNbaPicker(nba.date ?? undefined);
    closeNbaPads();
    if (version === 2)
        (0, nba_tap_handler_v2_1.openNbaTapPadV2)(gameId, teams, onExit, { espnPreselect: espnTip });
    else
        (0, nba_tap_handler_1.openNbaTapPad)(gameId, teams, onExit);
}
/** The recorder's game picker (§11.2): the America/New_York day's games from the server. */
function showNbaPicker(date = (0, nba_picker_1.easternYmd)(Date.now())) {
    const mine = ++view;
    nba.date = date;
    showLeagueSelector(true, "nba");
    stopPolling();
    (0, tap_pad_handler_1.closeTapPad)();
    closeNbaPads();
    (0, hockey_pad_handler_1.closeHockeyPad)();
    page.gameId = null;
    showBack(true);
    (0, page_navigation_1.syncLocation)("win-prob", ["nba"]);
    setStatus("NBA recorder");
    const body = el("win-prob-body");
    if (!body)
        return;
    body.innerHTML = (0, nba_picker_1.renderNbaPicker)({ date, status: "loading" });
    (0, nba_schedule_1.fetchNbaSchedule)(date)
        .then((schedule) => { if (view === mine)
        body.innerHTML = (0, nba_picker_1.renderNbaPicker)({ date, status: "ok", schedule }); })
        .catch(() => { if (view === mine)
        body.innerHTML = (0, nba_picker_1.renderNbaPicker)({ date, status: "unavailable" }); });
}
/**
 * Entry point for a `/app/win-prob/...` URL. `query` is the route applier's allowed query (the
 * NBA pad's teams), kept through login; without one, the address's own.
 */
function openWinProbRoute(segments, query) {
    const [league, gameId, kind] = segments;
    void loadLeagues().catch(() => { }); // the selector's options, for whichever view opens; openFirstLeague reports a failure
    if (league === "nba" && gameId && kind === "tap") {
        const search = query ?? window.location.search;
        showNbaTapPad(gameId, (0, nba_tap_handler_1.teamsFromQuery)(search), (0, nba_tap_handler_1.nbaPadFromQuery)(search));
        return;
    }
    if (league === "nba" && !gameId) {
        showNbaPicker();
        return;
    }
    if (league === "nhl" && gameId && (kind === "pad" || kind === undefined)) {
        showHockeyPad(gameId);
        return;
    }
    if (league && gameId && kind === "tap" && LEAGUES.includes(league)) {
        showTapPad(league, gameId);
        return;
    }
    if (league && gameId && LEAGUES.includes(league)) {
        showWinProbGame(league, gameId);
        return;
    }
    openFirstLeague(LEAGUES.includes(league) ? league : undefined);
}
/** Called when the page is left, so the timer does not outlive the view. */
function teardownWinProbPage() {
    view++; // a schedule or leagues answer still in flight draws nothing
    stopPolling();
    (0, tap_pad_handler_1.closeTapPad)();
    closeNbaPads();
    (0, hockey_pad_handler_1.closeHockeyPad)();
}
/**
 * `onExit` is where the list's back button goes (the events page). Injected
 * rather than imported because navigation-service already imports this file.
 */
function setupWinProbPage(opts = {}) {
    (0, tap_pad_handler_1.setupTapPad)();
    (0, nba_tap_handler_1.setupNbaTapPad)();
    (0, hockey_pad_handler_1.setupHockeyPad)();
    const body = el("win-prob-body");
    body?.addEventListener("input", (event) => {
        const input = event.target.closest(".wp-qb-name");
        const side = input?.dataset.side;
        if (input && (side === "home" || side === "away"))
            page.qbNameDrafts[side] = input.value;
    });
    body?.addEventListener("click", (event) => {
        const target = event.target;
        const pick = target.closest(".nba-pick");
        if (pick) {
            const choice = target.closest(".nba-pick-pad"); // the row outside its buttons opens nothing
            const { gameId, home, away, state } = pick.dataset;
            if (choice && gameId && /^\d+$/.test(gameId) && home && away && TEAM.test(home) && TEAM.test(away)) {
                const version = choice.dataset.pad === "2" ? 2 : 1;
                const espnTip = state === "pre" ? true : state === "in" || state === "post" ? false : null;
                showNbaTapPad(gameId, { home: home.toUpperCase(), away: away.toUpperCase() }, version, espnTip);
            }
            return;
        }
        const day = target.closest(".nba-pick-day");
        if (day) {
            if ((0, nba_picker_1.isYmd)(day.dataset.date))
                showNbaPicker(day.dataset.date);
            return;
        }
        const personnelButton = target.closest(".wp-personnel");
        if (personnelButton && page.gameId && page.league === "nfl" && !page.personnelPending) {
            const kind = personnelButton.dataset.kind;
            let side = personnelButton.dataset.side;
            let correctsCommandId = null;
            if (kind === "correction") {
                const events = page.latestView?.personnelEvents ?? [];
                const corrected = new Set(events.filter((e) => e.kind === "correction").map((e) => e.correctsCommandId));
                const last = [...events].reverse().find((e) => e.kind !== "correction" && !corrected.has(e.commandId));
                if (!last)
                    return;
                side = last.side;
                correctsCommandId = last.commandId;
            }
            if (!side)
                return;
            const name = body.querySelector(`.wp-qb-name[data-side="${side}"]`)?.value.trim() || null;
            const gameId = page.gameId;
            page.personnelPending = true;
            page.localPersonnelWarning = true;
            const readiness = body.querySelector(".wp-readiness");
            if (readiness) {
                readiness.textContent = "DO NOT QUOTE · recording QB status";
                readiness.classList.remove("wp-ready");
                readiness.classList.add("wp-do-not-quote");
            }
            const edge = body.querySelector(".wp-edge-value");
            if (edge)
                edge.textContent = "—";
            setStatus("Recording QB status…");
            void (0, win_prob_1.recordWinProbPersonnel)("nfl", gameId, { commandId: (0, uuid_1.uuid)(), kind, side, observedAt: new Date().toISOString(), playerOut: kind === "qb_exit_suspected" ? name : null, playerIn: kind === "backup_confirmed" ? name : null, correctsCommandId })
                .then(() => { if (page.gameId === gameId && page.league === "nfl")
                page.localPersonnelWarning = false; })
                .catch((error) => { if (page.gameId === gameId && page.league === "nfl")
                setStatus(`QB status not confirmed: ${error.message}`); })
                .finally(() => { page.personnelPending = false; if (page.gameId === gameId && page.league === "nfl")
                void refreshGame(); });
            return;
        }
        // Track before open: the track button sits inside the row, so an
        // open-first check would swallow every track tap.
        const track = target.closest(".wp-track");
        if (track) {
            const { league, gameId } = track.dataset;
            if (!league || !gameId)
                return;
            track.setAttribute("disabled", "true");
            void (0, win_prob_1.trackWinProbGame)(league, gameId)
                .then(() => refreshList())
                .catch((error) => {
                track.removeAttribute("disabled");
                setStatus(`could not track: ${error.message}`);
            });
            return;
        }
        const padLink = target.closest(".wp-open-tap");
        if (padLink) {
            const { league, gameId } = padLink.dataset;
            if (league && gameId)
                showTapPad(league, gameId);
            return;
        }
        const open = target.closest(".wp-open");
        if (!open)
            return;
        const { league, gameId } = open.dataset;
        if (league && gameId)
            showWinProbGame(league, gameId);
    });
    // One button: a game goes back to the list, the list leaves the page.
    el("win-prob-back")?.addEventListener("click", () => {
        if (page.gameId !== null)
            showWinProbList();
        else
            opts.onExit?.();
    });
    void (0, server_kind_1.isRecorderServer)().then((recorder) => {
        offer.exit = !recorder;
        if (topLevel)
            showBack(true);
    });
    const select = el("win-prob-league");
    select?.addEventListener("change", () => {
        const league = select.value;
        if (league === "nba")
            showNbaPicker();
        else if (LEAGUES.includes(league)) {
            page.league = league;
            showWinProbList();
        }
    });
}
